import React, { useState, useEffect, useCallback } from 'react';
import { apiErrorText } from '../lib/apiError';
import { Webhook, Plus, Loader2, Trash2, FileText } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import TabPageLayout from './TabPageLayout';
import apiV2 from '../utils/apiV2';

// Marketing → Webhooks. Moved out of the Settings dialog (Aug 2026) so it sits
// next to Integrations — same backend endpoints as before
// (/organizations/{orgId}/settings/webhooks).

const EVENT_DEFS = [
  ['booking.created', 'New booking received'],
  ['booking.updated', 'Booking details changed'],
  ['booking.cancelled', 'Booking cancelled'],
];

const WebhooksTab = () => {
  const orgId = localStorage.getItem('organizationId');
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [newUrl, setNewUrl] = useState('');
  const [newEvents, setNewEvents] = useState({
    'booking.created': true,
    'booking.updated': false,
    'booking.cancelled': false,
  });
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState(null);

  const fetchWebhooks = useCallback(async () => {
    if (!orgId) return;
    try {
      setLoading(true);
      const response = await apiV2.get(`/organizations/${orgId}/settings/webhooks`);
      setWebhooks(response.data.webhooks || []);
    } catch (error) {
      console.error('Error fetching webhooks:', error);
      toast.error('Failed to load webhooks');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { fetchWebhooks(); }, [fetchWebhooks]);

  const handleCreate = async () => {
    if (!newUrl.trim()) {
      toast.error('Please enter a webhook URL');
      return;
    }
    if (!newUrl.startsWith('http://') && !newUrl.startsWith('https://')) {
      toast.error('Webhook URL must start with http:// or https://');
      return;
    }
    const selectedEvents = Object.keys(newEvents).filter((event) => newEvents[event]);
    if (selectedEvents.length === 0) {
      toast.error('Select at least one event');
      return;
    }
    try {
      setSaving(true);
      await apiV2.post(`/organizations/${orgId}/settings/webhooks`, {
        name: `Webhook ${webhooks.length + 1}`,
        url: newUrl,
        events: selectedEvents,
      });
      toast.success('Webhook created!');
      setNewUrl('');
      setNewEvents({ 'booking.created': true, 'booking.updated': false, 'booking.cancelled': false });
      setShowNewModal(false);
      fetchWebhooks();
    } catch (error) {
      console.error('Error creating webhook:', error);
      toast.error(apiErrorText(error, 'Failed to create webhook'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (webhookId) => {
    if (!window.confirm('Are you sure you want to delete this webhook?')) return;
    try {
      await apiV2.delete(`/organizations/${orgId}/settings/webhooks/${webhookId}`);
      toast.success('Webhook deleted');
      fetchWebhooks();
    } catch (error) {
      console.error('Error deleting webhook:', error);
      toast.error('Failed to delete webhook');
    }
  };

  const handleTest = async (webhookId) => {
    try {
      setTestingId(webhookId);
      const response = await apiV2.post(`/organizations/${orgId}/settings/webhooks/${webhookId}/test`);
      const status = response.data.status_code;
      if (response.data.success) {
        toast.success(`Webhook delivered — your endpoint replied ${status}`);
      } else if (status >= 400) {
        toast.error(`Your endpoint responded ${status}. Check that the URL is reachable.`);
      } else {
        toast.error(response.data.message || 'Webhook test failed');
      }
    } catch (error) {
      console.error('Error testing webhook:', error);
      toast.error(apiErrorText(error, 'Could not reach the webhook endpoint'));
    } finally {
      setTestingId(null);
    }
  };

  const handleToggle = async (webhookId, currentStatus) => {
    try {
      await apiV2.patch(`/organizations/${orgId}/settings/webhooks/${webhookId}`, {
        is_active: !currentStatus,
      });
      toast.success(currentStatus ? 'Webhook disabled' : 'Webhook enabled');
      fetchWebhooks();
    } catch (error) {
      console.error('Error toggling webhook:', error);
      toast.error('Failed to update webhook');
    }
  };

  return (
    <TabPageLayout
      title="Webhooks"
      subtitle="Receive HTTPS POSTs in real time when bookings change — wire BookingFlows into your own systems"
      loading={loading && webhooks.length === 0}
      actions={
        <Button
          onClick={() => setShowNewModal(true)}
          size="sm"
          style={{ backgroundColor: '#2563EB' }}
          className="hover:opacity-90 h-9 text-white"
          data-testid="webhooks-add-btn"
        >
          <Plus className="w-3.5 h-3.5 mr-1" />
          Add Webhook
        </Button>
      }
    >
      <div className="max-w-xl space-y-5">
        {/* Webhook list */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        ) : webhooks.length === 0 ? (
          <div className="card-soft p-10 text-center" data-testid="webhooks-empty">
            <Webhook className="w-10 h-10 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600 font-medium">No webhooks configured</p>
            <p className="text-xs text-gray-500 mt-1">Add your first to start receiving event notifications</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="webhooks-list">
            {webhooks.map((webhook) => (
              <div key={webhook.id} className="card-soft p-4" data-testid={`webhook-row-${webhook.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-md border ${
                        webhook.isActive
                          ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                          : 'border-gray-200 bg-gray-50 text-gray-600'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${webhook.isActive ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                        {webhook.isActive ? 'Active' : 'Inactive'}
                      </span>
                      <button
                        onClick={() => handleToggle(webhook.id, webhook.isActive)}
                        className="text-xs text-blue-600 hover:text-blue-700"
                      >
                        {webhook.isActive ? 'Disable' : 'Enable'}
                      </button>
                    </div>
                    <p className="text-sm font-mono text-gray-900 break-all mb-2">{webhook.url}</p>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {webhook.events.map((event) => (
                        <span key={event} className="text-[11px] font-mono px-2 py-0.5 rounded-md border border-gray-200 text-gray-700 bg-white">
                          {event}
                        </span>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500">
                      Created {new Date(webhook.createdAt).toLocaleDateString()}
                      {webhook.lastTriggeredAt && (
                        <span> • Last triggered {new Date(webhook.lastTriggeredAt).toLocaleString()}</span>
                      )}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleTest(webhook.id)}
                      disabled={testingId === webhook.id || !webhook.isActive}
                      variant="outline"
                      size="sm"
                      className="h-8"
                      data-testid={`webhook-test-${webhook.id}`}
                    >
                      {testingId === webhook.id ? (
                        <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Testing...</>
                      ) : (
                        'Test'
                      )}
                    </Button>
                    <Button
                      onClick={() => handleDelete(webhook.id)}
                      variant="outline"
                      size="sm"
                      className="h-8 text-red-600 hover:text-red-700"
                      data-testid={`webhook-delete-${webhook.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Available events */}
        <div className="card-soft p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Available events</h3>
          <div className="space-y-1.5">
            {EVENT_DEFS.map(([evt, desc]) => (
              <div key={evt} className="flex items-center justify-between text-sm">
                <span className="font-mono text-[12px] px-2 py-0.5 rounded-md border border-gray-200 text-gray-700">{evt}</span>
                <span className="text-xs text-gray-500">{desc}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-3">
            Prefer no-code? The Zapier connection under Integrations pushes the same moments into 8,000+ apps.
          </p>
        </div>

        {/* Payload example */}
        <div className="card-soft p-5">
          <details>
            <summary className="text-sm font-medium text-gray-900 cursor-pointer select-none flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-600" />
              Sample payload
            </summary>
            <pre className="text-xs bg-gray-900 text-gray-100 p-3 rounded-lg overflow-x-auto mt-3">
{`{
  "event": "booking.created",
  "timestamp": "2026-01-17T10:30:00Z",
  "data": {
    "id": "bkg_abc123",
    "confirmationCode": "ABC123",
    "customerName": "John Doe",
    "customerEmail": "john@example.com",
    "serviceName": "Sunset Cruise",
    "date": "2026-02-15",
    "timeSlot": "6:00 PM - 8:00 PM",
    "quantity": 2,
    "totalPrice": 150.00,
    "paymentStatus": "paid"
  }
}`}
            </pre>
          </details>
        </div>

        {/* New webhook modal */}
        <Dialog open={showNewModal} onOpenChange={setShowNewModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add New Webhook</DialogTitle>
              <DialogDescription>
                Enter the URL where you want to receive event notifications
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-sm">Webhook URL <span className="text-red-500">*</span></Label>
                <Input
                  type="url"
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                  placeholder="https://your-server.com/webhook"
                  className="mt-1"
                  data-testid="webhook-url-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Need a test URL? Use <a href="https://webhook.site" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">webhook.site</a> to get a free endpoint
                </p>
              </div>
              <div>
                <Label className="text-sm mb-2 block">Events to Subscribe <span className="text-red-500">*</span></Label>
                <div className="space-y-2">
                  {EVENT_DEFS.map(([evt, desc]) => (
                    <label key={evt} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!newEvents[evt]}
                        onChange={(e) => setNewEvents({ ...newEvents, [evt]: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">{desc}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowNewModal(false)}>Cancel</Button>
              <Button
                onClick={handleCreate}
                disabled={saving}
                style={{ backgroundColor: '#2563EB' }}
                className="hover:opacity-90 text-white"
                data-testid="webhook-create-btn"
              >
                {saving ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</>
                ) : (
                  'Create Webhook'
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </TabPageLayout>
  );
};

export default WebhooksTab;
